import { useEffect, useState } from "react";
import { ChevronDown, Facebook, Instagram, Menu, MessageCircle, X } from "lucide-react";

import { FACEBOOK, INSTAGRAM, WHATSAPP } from "@/lib/contacto";
import { track } from "@/lib/analytics";
import { ResponsiveImage } from "./ResponsiveImage";
import { CATEGORIAS_PRODUCTO } from "@/lib/productos";

const logo = { url: "/img/logotipo-pyme-visible-horizontal.png" };

const links = [
  { href: "/#inicio", label: "Inicio" },
  { href: "/casos", label: "Casos reales" },
  { href: "/#proceso", label: "Cómo funciona" },
  { href: "/#nosotros", label: "Sobre nosotros" },
  { href: "/#contacto", label: "Contacto" },
];

const rutasPorCategoria = {
  stands: "/stand-nfc-qr",
  tarjetas: "/tarjeta-nfc-qr",
  llaveros: "/llavero-nfc-qr",
  personalizados: "/stand-personalizado-nfc-qr",
};

const productos = CATEGORIAS_PRODUCTO.map((categoria) => ({
  href: rutasPorCategoria[categoria.id],
  label: categoria.label,
}));

export function Header({ productLinksNewTab = true }: { productLinksNewTab?: boolean }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-transparent bg-canvas/70 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-ink focus:px-5 focus:py-3 focus:text-sm focus:font-semibold focus:text-white"
      >
        Saltar al contenido
      </a>

      <div
        className={`pv-header-shell mx-3 my-2 flex min-h-[4rem] max-w-[1320px] items-center justify-between gap-4 px-3.5 py-2 sm:mx-6 sm:my-3 sm:min-h-[5rem] sm:px-5 lg:mx-auto lg:px-6 ${
          scrolled ? "pv-header-shell--scrolled" : ""
        }`}
      >
        <a href="/" className="flex shrink-0 items-center" aria-label="Pyme Visible - Inicio">
          <ResponsiveImage
            sizes="(max-width: 639px) 120px, 192px"
            src={logo.url}
            width={2172}
            height={724}
            alt="Logotipo de Pyme Visible"
            className="h-10 w-auto sm:h-16"
          />
        </a>

        <nav className="hidden items-center gap-2 lg:flex" aria-label="Navegación principal">
          <div className="pv-header-nav flex items-center gap-1 rounded-full p-1.5">
            <a className="pv-nav-link" href="/#inicio">
              Inicio
            </a>

            <details className="group relative">
              <summary className="pv-nav-link flex cursor-pointer list-none items-center gap-1.5 [&::-webkit-details-marker]:hidden">
                Productos
                <ChevronDown
                  className="h-3.5 w-3.5 transition-transform duration-300 group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="absolute left-1/2 top-[calc(100%+14px)] w-72 -translate-x-1/2 rounded-3xl bg-white p-3 text-sm ring-1 ring-ink/10">
                <p className="px-4 pb-2 pt-1 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-slate">
                  Productos NFC + QR
                </p>
                {productos.map((producto) => (
                  <a
                    key={producto.href}
                    href={producto.href}
                    target={productLinksNewTab ? "_blank" : undefined}
                    rel={productLinksNewTab ? "noopener noreferrer" : undefined}
                    className="block rounded-2xl px-4 py-3 font-medium text-ink transition-colors hover:bg-sky"
                  >
                    {producto.label}
                  </a>
                ))}
              </div>
            </details>

            {links.slice(1).map((link) => (
              <a key={link.href} className="pv-nav-link" href={link.href}>
                {link.label}
              </a>
            ))}
          </div>
        </nav>

        <a
          href="/productos"
          target={productLinksNewTab ? "_blank" : undefined}
          rel={productLinksNewTab ? "noopener noreferrer" : undefined}
          onClick={() => track("product_catalog_click", { ubicacion: "header" })}
          className="pv-button pv-button--primary hidden min-h-11 shrink-0 items-center px-5 py-3 text-sm lg:inline-flex"
        >
          Ver productos
        </a>

        <button
          type="button"
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="pv-header-menu grid h-10 w-10 shrink-0 place-items-center rounded-xl text-ink sm:h-11 sm:w-11 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="pv-mobile-nav mx-3 rounded-3xl px-5 pb-7 pt-3 sm:mx-6 lg:hidden">
          <nav className="mx-auto flex max-w-[1320px] flex-col" aria-label="Navegación móvil">
            <a
              href="/#inicio"
              onClick={() => setOpen(false)}
              className="border-b border-ink/10 py-3.5 text-base font-medium text-ink"
            >
              Inicio
            </a>
            <p className="pt-4 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-slate">
              Productos
            </p>
            <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-2">
              {productos.map((producto) => (
                <a
                  key={producto.href}
                  href={producto.href}
                  target={productLinksNewTab ? "_blank" : undefined}
                  rel={productLinksNewTab ? "noopener noreferrer" : undefined}
                  onClick={() => setOpen(false)}
                  className="rounded-xl bg-white px-4 py-3 text-sm font-medium text-ink"
                >
                  {producto.label}
                </a>
              ))}
            </div>
            {links.slice(1).map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="border-b border-ink/10 py-3.5 text-base font-medium text-ink"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/productos"
              target={productLinksNewTab ? "_blank" : undefined}
              rel={productLinksNewTab ? "noopener noreferrer" : undefined}
              onClick={() => {
                setOpen(false);
                track("product_catalog_click", { ubicacion: "menu_mobile" });
              }}
              className="pv-button pv-button--primary mt-5 inline-flex min-h-11 items-center justify-center px-5 py-3.5 text-base"
            >
              Ver productos NFC
            </a>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setOpen(false);
                track("whatsapp_click", { ubicacion: "menu_mobile" });
              }}
              className="pv-button pv-button--secondary mt-3 inline-flex min-h-11 items-center justify-center gap-2 px-5 py-3.5 text-base"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Consultar por WhatsApp
            </a>
            <div className="mt-5 flex gap-3">
              {[
                {
                  href: INSTAGRAM,
                  label: "Instagram de Pyme Visible",
                  red: "instagram",
                  Icon: Instagram,
                },
                {
                  href: FACEBOOK,
                  label: "Facebook de Pyme Visible",
                  red: "facebook",
                  Icon: Facebook,
                },
              ].map(({ href, label, red, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  onClick={() => track("social_click", { red, ubicacion: "menu_mobile" })}
                  className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 text-ink"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
